// Story 7.6 — shared primitives for the feed card taxonomy.
//
// Every card shape (EditorialCard, CreatorPostCard, …) composes from
// these: the provenance chip (the "honesty seal" footer — its preposition
// vocabulary is deliberately closed per Sally's rule: "Posted by" /
// "About" / "via" / "Editorial · Greenhold", nothing creative), the
// author/subject top-row slots (author identity top-left, subject-hood
// top-right — whoever occupies top-left is claiming the words), and the
// creator mark (initial-circle + @handle linking to the public Astro
// profile — one of the only three permitted per-card interactions).

import type { ReactNode } from "react";

/** Astro public-site origin — mirrors AccountProfilePage's resolution. */
const PUBLIC_SITE_ORIGIN =
  (typeof window !== "undefined" &&
    (import.meta.env.VITE_PUBLIC_SITE_ORIGIN as string | undefined)) ||
  (typeof window !== "undefined" ? window.location.origin : "");

export function publicProfileUrl(handle: string): string {
  return `${PUBLIC_SITE_ORIGIN}/u/${handle}`;
}

/** Friendly display name per content_sources.source_kind (migration 0042). */
export function sourceDisplayName(kind: string): string {
  switch (kind) {
    case "admin_editorial":
    case "system_silence":
    case "platform_dossier":
      return "Greenhold";
    case "bulletin":
      return "Greenhold Bulletin";
    case "creator_post":
      return "Creator";
    case "twitch_clip":
    case "twitch_popular":
      return "Twitch";
    case "youtube_short":
    case "youtube_popular":
      return "YouTube";
    case "kick_clip":
      return "Kick";
    case "hltv_rss":
      return "HLTV.org";
    case "lolesports_json":
      return "LoL Esports";
    default:
      return kind;
  }
}

/** "just now" / "12m ago" / "5h ago" / "3d ago" / "12 Jun 2026". */
export function relativeTime(iso: string, nowMs = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Stable wear-variant seed (0-2) derived from a card id. ParchmentPost
 * cycles its parchment aging via a module counter when `seed` is omitted;
 * feed cards pass this instead so a card renders identically across
 * remounts (the promo-invisibility equality guard depends on it) while
 * neighbours — different ids — still differ.
 */
export function wearSeed(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 3;
}

/**
 * Link-out CTA styling for parchment cards (Read on … / Watch on … / View
 * dossier). Gold-700 reads as an affordance on the parchment ground where
 * the feed's dark-ground gold-300 would wash out.
 */
export const parchmentCtaClass =
  "font-body text-sm font-medium text-brand-gold-700 underline underline-offset-2 decoration-brand-gold-600/40 hover:text-brand-gold-800";

/**
 * Creator identity as a plain-text link to the public Astro profile — the
 * parchment-era successor to CreatorMark's avatar circle. Sits in the mono
 * author (or subject) slot of a ParchmentPost; one of the three permitted
 * per-card interactions (AC 9).
 */
export function ProfileLink({
  handle,
  label,
}: {
  handle: string;
  label: string;
}) {
  return (
    <a
      href={publicProfileUrl(handle)}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-ink-700 hover:text-brand-ink-900"
      data-testid="creator-mark"
    >
      {label}
    </a>
  );
}

/**
 * Provenance chip — the footer honesty seal. Callers pass the full
 * pre-composed text ("Posted by @x · 2h ago") so the preposition
 * discipline stays visible at each card's call site.
 *
 * Retained for non-feed consumers; feed cards now render the honesty seal
 * through ParchmentPost's printed-document footer.
 */
export function ProvenanceChip({ children }: { children: ReactNode }) {
  return (
    <p
      className="m-0 mt-4 font-body text-xs text-brand-paper-100/50"
      data-testid="provenance-chip"
    >
      {children}
    </p>
  );
}

/** Author-top-left / subject-top-right header row (Sally's header rule). */
export function CardTopRow({
  left,
  right,
}: {
  left: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2" data-testid="card-author">
        {left}
      </div>
      {right ? (
        <div
          className="flex shrink-0 items-center gap-2"
          data-testid="card-subject"
        >
          {right}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Creator identity mark: initial-circle + @handle. `size="sm"` is the
 * top-right subject-hood indicator on curated cards. Links out to the
 * public Astro profile (allowed interaction (b) per AC 9).
 */
export function CreatorMark({
  handle,
  displayName,
  size = "md",
}: {
  handle: string | null;
  displayName: string | null;
  size?: "sm" | "md";
}) {
  const label = handle ? `@${handle}` : (displayName ?? "");
  const initial = (displayName ?? handle ?? "?").trim().charAt(0).toUpperCase();
  const circle =
    size === "sm"
      ? "h-5 w-5 text-[0.6rem]"
      : "h-7 w-7 text-sm";

  const inner = (
    <>
      <span
        className={`flex ${circle} items-center justify-center rounded-full bg-brand-gold-500/20 font-heading text-brand-gold-300`}
        aria-hidden
      >
        {initial}
      </span>
      {label ? (
        <span className="truncate font-body text-sm text-brand-paper-100/90">
          {label}
        </span>
      ) : null}
    </>
  );

  if (!handle) {
    return <span className="flex min-w-0 items-center gap-2">{inner}</span>;
  }
  return (
    <a
      href={publicProfileUrl(handle)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-w-0 items-center gap-2 hover:brightness-110"
      data-testid="creator-mark"
    >
      {inner}
    </a>
  );
}
